import { Button, ButtonGroup } from '@mui/material';
import { useMemo } from 'react';

export type DatePreset = 'today' | 'week' | 'month' | 'all';

interface PresetOption {
  key: DatePreset;
  label: string;
}

interface DatePresetSelectorProps {
  value: DatePreset;
  onChange: (preset: DatePreset) => void;
  /** Defaults to ['today', 'week', 'month', 'all'] */
  presets?: DatePreset[];
}

const DEFAULT_PRESETS: PresetOption[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
  { key: 'all', label: 'Tudo' },
];

export const DatePresetSelector: React.FC<DatePresetSelectorProps> = ({
  value,
  onChange,
  presets: presetsConfig,
}) => {
  const options = useMemo(() => {
    if (!presetsConfig) return DEFAULT_PRESETS;
    return DEFAULT_PRESETS.filter(p => presetsConfig.includes(p.key));
  }, [presetsConfig]);

  return (
    <ButtonGroup size='small' fullWidth>
      {options.map(p => (
        <Button
          key={p.key}
          variant={value === p.key ? 'contained' : 'outlined'}
          onClick={() => onChange(p.key)}
        >
          {p.label}
        </Button>
      ))}
    </ButtonGroup>
  );
};

/**
 * Calculate date range from a preset. Returns { start, end } Date objects.
 */
export function getPresetRange(preset: DatePreset): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  if (preset === 'today') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === 'week') {
    // Semana começa na Segunda-feira (padrão brasileiro)
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (preset === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else {
    start.setFullYear(2000, 0, 1);
    start.setHours(0, 0, 0, 0);
    end.setFullYear(2099, 11, 31);
    end.setHours(23, 59, 59, 999);
  }
  return { start, end };
}
